import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import PropTypes from 'prop-types'
import {
  loadConfig,
  loadLocations,
  loadAttributes,
  loadFacilities,
} from '../utils/dataLoader'

const DataContext = createContext()

const initialState = {
  config: null,
  locations: null,
  attributes: {},
  facilities: {},
  images: {},
  loading: {
    config: true,
    locations: true,
    attributes: {},
    facilities: {},
    images: false,
  },
  errors: {},
  lastLoaded: {},
}

function dataReducer(state, action) {
  switch (action.type) {
    case 'LOAD_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.key]: true,
        },
      }

    case 'LOAD_SUCCESS':
      // Handle facilities data specially
      if (action.key.startsWith('facilities_')) {
        const category = action.key.replace('facilities_', '')
        return {
          ...state,
          facilities: {
            ...state.facilities,
            [category]: action.data,
          },
          loading: {
            ...state.loading,
            [action.key]: false,
          },
          lastLoaded: {
            ...state.lastLoaded,
            [action.key]: Date.now(),
          },
          errors: {
            ...state.errors,
            [action.key]: null,
          },
        }
      }

      // Handle attributes data specially
      if (action.key.startsWith('attributes_')) {
        const category = action.key.replace('attributes_', '')
        return {
          ...state,
          attributes: {
            ...state.attributes,
            [category]: action.data,
          },
          loading: {
            ...state.loading,
            [action.key]: false,
          },
          lastLoaded: {
            ...state.lastLoaded,
            [action.key]: Date.now(),
          },
          errors: {
            ...state.errors,
            [action.key]: null,
          },
        }
      }

      // Handle other data types normally
      return {
        ...state,
        [action.key]: action.data,
        loading: {
          ...state.loading,
          [action.key]: false,
        },
        lastLoaded: {
          ...state.lastLoaded,
          [action.key]: Date.now(),
        },
        errors: {
          ...state.errors,
          [action.key]: null,
        },
      }

    case 'LOAD_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.key]: false,
        },
        errors: {
          ...state.errors,
          [action.key]: action.error,
        },
      }

    default:
      return state
  }
}

export function DataProvider({ children }) {
  const [state, dispatch] = useReducer(dataReducer, initialState)
  const loadingRef = useRef(new Set()) // Track what's currently loading

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      // Load config
      try {
        dispatch({ type: 'LOAD_START', key: 'config' })
        const config = await loadConfig()
        dispatch({ type: 'LOAD_SUCCESS', key: 'config', data: config })
      } catch (error) {
        dispatch({ type: 'LOAD_ERROR', key: 'config', error })
      }

      // Load locations
      try {
        dispatch({ type: 'LOAD_START', key: 'locations' })
        const locations = await loadLocations()
        dispatch({ type: 'LOAD_SUCCESS', key: 'locations', data: locations })
      } catch (error) {
        dispatch({ type: 'LOAD_ERROR', key: 'locations', error })
      }

      // Load default attributes (health)
      try {
        dispatch({ type: 'LOAD_START', key: 'attributes' })
        const healthAttributes = await loadAttributes('health')
        const educationAttributes = await loadAttributes('education')
        dispatch({
          type: 'LOAD_SUCCESS',
          key: 'attributes',
          data: { health: healthAttributes, education: educationAttributes },
        })
      } catch (error) {
        dispatch({ type: 'LOAD_ERROR', key: 'attributes', error })
      }
    }

    loadInitialData()
  }, [])

  const isStale = useCallback(timestamp => {
    const CACHE_DURATION = 30 * 60 * 1000 // 30 minutes
    return !timestamp || Date.now() - timestamp > CACHE_DURATION
  }, [])

  const loadFacilityData = useCallback(
    async category => {
      const key = `facilities_${category}`

      // Check if already loaded or currently loading
      if (
        loadingRef.current.has(key) ||
        (state.facilities[category] && !isStale(state.lastLoaded[key]))
      ) {
        return
      }

      try {
        loadingRef.current.add(key)
        dispatch({ type: 'LOAD_START', key })
        const facilities = await loadFacilities(category)
        dispatch({
          type: 'LOAD_SUCCESS',
          key,
          data: facilities,
        })
      } catch (error) {
        dispatch({ type: 'LOAD_ERROR', key, error })
      } finally {
        loadingRef.current.delete(key)
      }
    },
    [isStale]
  )

  const loadAttributesData = useCallback(
    async category => {
      const key = `attributes_${category}`

      // Check if already loaded or currently loading
      if (
        loadingRef.current.has(key) ||
        (state.attributes[category] && !isStale(state.lastLoaded[key]))
      ) {
        return
      }

      try {
        loadingRef.current.add(key)
        dispatch({ type: 'LOAD_START', key })
        const attributes = await loadAttributes(category)
        dispatch({
          type: 'LOAD_SUCCESS',
          key,
          data: attributes,
        })
      } catch (error) {
        dispatch({ type: 'LOAD_ERROR', key, error })
      } finally {
        loadingRef.current.delete(key)
      }
    },
    [isStale]
  )

  const isLoading = useCallback(
    key => state.loading[key] || false,
    [state.loading]
  )
  const hasError = useCallback(key => !!state.errors[key], [state.errors])

  const value = {
    ...state,
    loadFacilityData,
    loadAttributesData,
    isLoading,
    hasError,
  }

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}

export function useData() {
  const context = useContext(DataContext)
  if (!context) {
    throw new Error('useData must be used within a DataProvider')
  }
  return context
}

DataProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
